import { Select, Switch } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import {
  rateSlabsKey,
  useItemCategories,
  useItemUnits,
  useItemVariants,
  useItems,
  type RateSlabRow,
} from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { ConfirmButton } from '@/components/ConfirmButton';
import { Field, controlClass } from '@/components/Field';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { IconAction } from '@/components/IconAction';
import { Button, Card, Notice } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { formatAmount } from '@core/format/money';
import { resolveSlabCharge, slabLabel } from '@core/bookings/pricing';
import type { Session } from '@core/session/types';
import {
  boundIsTaken,
  cardProblems,
  groupIntoCards,
  ladder,
  ownerLabels,
  stepLabel,
  type OwnerKind,
} from './cards';

/**
 * One rate card, on a full page.
 *
 * <p>
 * <strong>A page rather than a drawer, and it replaces the list rather than sitting over it.</strong>
 * A card is a ladder of bands, each with its own controls, plus the owner it prices and a preview of
 * what it charges — three sections that a 560px drawer showed a third of at a time, with the ladder
 * scrolled out of sight exactly when a shop is deciding whether the next band is right. This is the
 * move `NewBookingPage` and `StockIntakePage` already made, for the same reason, and the layout is
 * the template's own `inventory/addProduct`: breadcrumb and actions in the heading, an 8/4 split
 * below it, each section a white card on the canvas.
 * </p>
 *
 * <p>
 * <strong>Every write lands immediately rather than on a Save at the top.</strong> The API is
 * band-level — publish a band, edit a band, withdraw a band — and batching them would be a small
 * transaction on the client: four calls, the third refused for a bound that already exists, and a
 * card left half-published with nothing to roll it back. So the heading's button says Done, not
 * Save, and the page says so out loud rather than leaving a clerk hunting for a commit.
 * </p>
 *
 * <p>
 * <strong>The owner and the scope lock once this page has published a band</strong>, because they
 * are the card's identity — the server's filtered unique indexes are built on exactly that, and its
 * update endpoint deliberately takes neither. Moving a card to another owner is deleting one card
 * and publishing another.
 * </p>
 *
 * <p>
 * Keyboard-first like the rest: the cursor starts in the first control, Enter walks to the next, and
 * the band's Add button carries `data-enter-key-commit` so the last field hands focus to it. There
 * is no `<form>` here — the page renders confirmations that bring their own — so the handler goes on
 * the page's own element through `enterKeyScope`, which is what `enterKeyNavigation` documents for
 * exactly this case.
 * </p>
 */
export function RateCardPage({
  session,
  rows,
  initial,
  currency,
  onClose,
}: {
  session: Session;
  /**
   * Every visible band, not this card's. The page re-derives its own card from the live query on
   * each render, which is what makes it show its own writes — a card captured into state when the
   * row was clicked is a snapshot no refetch can reach.
   */
  rows: readonly RateSlabRow[];
  initial?: { ownerKind: OwnerKind; ownerId: number; shared: boolean } | undefined;
  currency: string;
  onClose: () => void;
}) {
  const [ownerKind, setOwnerKind] = useState<OwnerKind>(initial?.ownerKind ?? 'Category');
  const [ownerId, setOwnerId] = useState<number | undefined>(initial?.ownerId);
  // Shared is the ordinary case for pricing: a chain sets one card for the company and overrides it
  // at a shop that charges differently, which is the opposite way round from the catalogue.
  const [shared, setShared] = useState(initial?.shared ?? true);

  // Set once this page has published a band. Together with `initial` it is what locks the owner —
  // and it has to be this rather than "the card has bands", or picking an owner that already has a
  // card would trap the page on it: a mis-click while creating, with no way back but leaving.
  const [published, setPublished] = useState(false);

  // Which design the configuration or garment pickers are narrowed to. Only the two narrow levels
  // use it: a rail of six hundred garments is not a dropdown, and "which design" is the question a
  // shop asks first anyway.
  const [designId, setDesignId] = useState<number | undefined>();

  // The cursor starts in the first control, as it does on every other data-entry surface.
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const categories = useItemCategories(session.branch?.id, true);
  // The same 200 the availability and units screens pick their design with. A catalogue past that
  // is real and wants a searchable endpoint; it would want one on those two screens first.
  const items = useItems(session.branch?.id, { includeInactive: true, pageSize: 200 });

  const variants = useItemVariants(ownerKind === 'Variant' ? designId : undefined);

  const units = useItemUnits(ownerKind === 'Unit' ? session.branch?.id : undefined, {
    itemId: designId,
    pageSize: 200,
  });

  const card = groupIntoCards(
    rows.filter((row) => row.shared === shared && ownerIdOn(row, ownerKind) === ownerId),
  )[0];

  const bands = card?.bands ?? [];
  const steps = ladder(bands);
  const locked = initial !== undefined || published;

  /*
    A shared band may only price an owner every branch can see. The server refuses the other way
    round (`RateSlab_SharedCannotPriceBranchOwner`) because such a card prices nothing at every
    other branch — silently, and the symptom is an unpriceable garment rather than an error. So the
    branch-local owners are not offered at all while the card is shared, rather than offered and
    then rejected.
  */
  const ownerOptions =
    ownerKind === 'Category'
      ? (categories.data ?? [])
          .filter((category) => !shared || category.isShared)
          .map((category) => ({
            label: `${'— '.repeat(category.depth)}${category.name}`,
            value: category.id,
          }))
      : ownerKind === 'Item'
        ? (items.data?.items ?? [])
            .filter((item) => !shared || item.isShared)
            .map((item) => ({ label: `${item.name} (${item.code})`, value: item.id }))
        : ownerKind === 'Variant'
          ? (variants.data ?? []).map((variant) => ({
              label: `${variant.displayName} · ${variant.code}`,
              value: variant.id,
            }))
          : // A garment hangs in one shop, so its card is that shop's — never the company's, which
            // is why the scope switch is forced off for this level below.
            (units.data?.items ?? []).map((unit) => ({
              label: `${unit.serialCode} · ${unit.variantDisplayName}`,
              value: unit.id,
            }));

  const chooseKind = (next: OwnerKind) => {
    setOwnerKind(next);
    setOwnerId(undefined);

    // A garment hangs on a rail in exactly one shop, so a company-wide card cannot name one — the
    // server refuses it, and offering the switch would be offering a write it would reject.
    if (next === 'Unit') {
      setShared(false);
    }
  };

  const chooseScope = (next: boolean) => {
    setShared(next);

    // Narrowing to the company loses any branch-local owner that was picked, which would otherwise
    // sit in the field looking chosen and be refused on save.
    if (next && ownerId !== undefined) {
      const stillOffered =
        ownerKind === 'Category'
          ? categories.data?.find((category) => category.id === ownerId)?.isShared
          : items.data?.items.find((item) => item.id === ownerId)?.isShared;

      if (stillOffered !== true) {
        setOwnerId(undefined);
      }
    }
  };

  // Whatever the picker is currently showing, so the heading names the card being edited. Falls back
  // to the card's own name, which is what an edit opened from the list already knows.
  const ownerName =
    ownerOptions.find((option) => option.value === ownerId)?.label ?? card?.ownerName;

  return (
    <>
      {/* ------------------------------------------------------------- heading */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 lg:mb-6">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to the rate cards"
            className="text-default hover:bg-light flex size-8 cursor-pointer items-center justify-center rounded hover:text-gray-900"
          >
            <i className="icon icon-arrow-left" />
          </button>

          <div>
            <div className="text-default mb-1 flex items-center gap-2 text-sm">
              <button type="button" onClick={onClose} className="hover:text-primary cursor-pointer">
                Rate cards
              </button>
              <i className="icon icon-chevron-right text-[10px]" />
              <span>{bands.length > 0 ? (ownerName ?? 'Card') : 'New'}</span>
            </div>

            <h1 className="page-title">
              {bands.length > 0 ? `Rate card: ${ownerName ?? ''}` : 'New rate card'}
            </h1>
            <p className="page-subtitle">
              What a garment costs for how long. Bookings are priced from this and refused without
              it.
            </p>
          </div>
        </div>

        {/* Done, not Save: every band on this page has already been written. */}
        <Button variant="primary" icon="icon icon-check" shortcut="back" onClick={onClose}>
          Done
        </Button>
      </div>

      {/*
        Enter moves to the next field and the band's Add button is the commit the last field hands
        focus to — see `enterKeyNavigation`. The handler goes on this element rather than on a
        `<form>`, because the confirmations rendered below bring their own.
      */}
      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-3">
        {/* ---------------------------------------------------------- left */}
        <div className="col-span-12 space-y-3 lg:col-span-8">
          <Card>
            <SectionTitle>What this card prices</SectionTitle>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Field label="Prices">
                  <Select
                    value={ownerKind}
                    onChange={chooseKind}
                    disabled={locked}
                    options={[
                      { label: 'A whole category', value: 'Category' },
                      { label: 'One design', value: 'Item' },
                      { label: 'One configuration', value: 'Variant' },
                      { label: 'One garment', value: 'Unit' },
                    ]}
                    className="w-full"
                  />
                </Field>
              </div>

              {/* The two narrow levels are picked inside a design — a shop with six hundred garments
                  has no useful dropdown of all of them, and "which design" is the first question. */}
              {(ownerKind === 'Variant' || ownerKind === 'Unit') && (
                <div className="col-span-2 sm:col-span-1">
                  <Field label="Design">
                    <Select
                      value={designId ?? null}
                      onChange={(value: number | null) => {
                        setDesignId(value ?? undefined);
                        setOwnerId(undefined);
                      }}
                      options={(items.data?.items ?? []).map((item) => ({
                        label: `${item.name} (${item.code})`,
                        value: item.id,
                      }))}
                      disabled={locked}
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      placeholder="Which design?"
                      className="w-full"
                    />
                  </Field>
                </div>
              )}

              <div className="col-span-2 sm:col-span-1">
                <Field label={ownerLabels[ownerKind]}>
                  <Select
                    value={ownerId ?? null}
                    onChange={(value: number | null) => setOwnerId(value ?? undefined)}
                    options={ownerOptions}
                    disabled={
                      locked || ((ownerKind === 'Variant' || ownerKind === 'Unit') && !designId)
                    }
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    placeholder={
                      ownerKind === 'Category'
                        ? 'Which category?'
                        : ownerKind === 'Item'
                          ? 'Which design?'
                          : ownerKind === 'Variant'
                            ? 'Which configuration?'
                            : 'Which garment?'
                    }
                    className="w-full"
                  />
                </Field>
              </div>

              <div className="col-span-2">
                <label className="border-border-color bg-light flex cursor-pointer items-start gap-2.5 rounded-md border p-3">
                  <Switch
                    size="small"
                    checked={shared}
                    disabled={locked || ownerKind === 'Unit'}
                    onChange={chooseScope}
                    className="mt-0.5"
                  />
                  <span className="text-[12.5px]">
                    <span className="font-semibold text-gray-900">Company-wide</span>
                    <span className="text-default">
                      {' '}
                      — every branch prices from this card. Off: only{' '}
                      {session.branch?.code ?? 'this branch'}, which overrides the company card
                      outright. Sharing needs a company-wide role.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {locked && (
              <p className="text-default mt-3 text-[11.5px]">
                Fixed once the card has a band — the owner and the scope are what identify it. To
                move a card, withdraw its bands and publish them against the new owner.
              </p>
            )}
          </Card>

          {ownerId === undefined ? (
            <Notice title="Choose what this card prices">
              Price a category and every design beneath it inherits — the nearest level with a card
              wins. One design's own card overrides its category's, a configuration's overrides
              that, and a single garment's overrides everything.
            </Notice>
          ) : (
            <>
              <Card>
                <SectionTitle
                  trailing={
                    <span className="text-default text-[11.5px]">
                      Read top down — a booking takes the first band long enough for it
                    </span>
                  }
                >
                  The ladder
                </SectionTitle>

                {steps.length === 0 ? (
                  <p className="border-border-color text-default rounded-md border border-dashed px-3 py-6 text-center text-[12.5px]">
                    No bands yet. The first one is the standard hire — "up to 3 days, ₹6,500".
                  </p>
                ) : (
                  <ul className="border-border-color divide-border-color divide-y rounded-md border">
                    {steps.map((step) => (
                      <BandRow
                        key={step.band.id}
                        session={session}
                        band={step.band}
                        label={stepLabel(step)}
                        currency={currency}
                      />
                    ))}
                  </ul>
                )}

                {card &&
                  cardProblems(card).map((problem) => (
                    <p key={problem} className="text-warning mt-3 text-[12px]">
                      <i className="icon icon-triangle-alert me-1" />
                      {problem}
                    </p>
                  ))}
              </Card>

              <BandEditor
                session={session}
                bands={bands}
                ownerKind={ownerKind}
                ownerId={ownerId}
                shared={shared}
                currency={currency}
                onPublished={() => setPublished(true)}
              />
            </>
          )}
        </div>

        {/* --------------------------------------------------------- right */}
        <div className="col-span-12 space-y-3 lg:col-span-4">
          <Preview bands={bands} currency={currency} />

          <Card>
            <SectionTitle>How a card resolves</SectionTitle>
            <ol className="text-default space-y-1.5 text-[12px]">
              <li>1. The garment's own card, if it has one</li>
              <li>2. Its configuration's — "Navy, size 46"</li>
              <li>3. Its design's</li>
              <li>4. Its category's, then up the tree</li>
            </ol>
            <p className="text-default mt-3 text-[11.5px]">
              The nearest level with a band wins the whole card. Size and garment adjustments apply
              on top only when the card came from a level above them.
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}

/** The template's section heading inside a card, with room for a note on the right. */
function SectionTitle({
  children,
  trailing,
}: {
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="text-title text-lg max-lg:text-[17px]">{children}</h2>
      {trailing}
    </div>
  );
}

/** One published band: what it covers, what it costs, and the two things that may be done to it. */
function BandRow({
  session,
  band,
  label,
  currency,
}: {
  session: Session;
  band: RateSlabRow;
  label: string;
  currency: string;
}) {
  const save = useApiMutation<RateSlabRow, Partial<RateSlabRow>>({
    mutationFn: (changes) =>
      apiFetch<RateSlabRow>(`/api/rate-slab/${band.id}`, {
        method: 'PUT',
        body: {
          amount: changes.amount ?? band.amount,
          perExtraDay: changes.perExtraDay === undefined ? band.perExtraDay : changes.perExtraDay,
          isActive: changes.isActive ?? band.isActive,
        },
      }),
    success: 'Band updated.',
    invalidates: [rateSlabsKey],
  });

  const remove = useApiMutation<void, number>({
    mutationFn: (id) => apiFetch<void>(`/api/rate-slab/${id}`, { method: 'DELETE' }),
    success: 'Band withdrawn.',
    invalidates: [rateSlabsKey],
  });

  const [amount, setAmount] = useState<number | null>(band.amount);
  const [perExtraDay, setPerExtraDay] = useState<number | null>(band.perExtraDay);
  const [open, setOpen] = useState(false);

  const changed = amount !== band.amount || perExtraDay !== band.perExtraDay;

  return (
    <li className={band.isActive ? '' : 'bg-light'}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{label}</span>
            {!band.isActive && <StatusBadge tone="warning">Retired</StatusBadge>}
          </div>
          <span className="text-default text-[11.5px]">
            {band.upToDays === null
              ? band.perExtraDay === null
                ? 'One price, however long it runs'
                : `Plus ${currency}${formatAmount(band.perExtraDay)} for each further day`
              : 'Flat for anything inside this span'}
          </span>
        </div>

        <span className="font-mono-nums text-title shrink-0 text-[15px] font-bold">
          {currency}
          {formatAmount(band.amount)}
        </span>

        <div className="flex shrink-0 gap-1">
          <WriteAction session={session} required="RateCard:Edit">
            {(disabled) => (
              <IconAction
                icon={open ? 'icon icon-chevron-up' : 'icon icon-pencil-line'}
                label={`Change the ${label} band`}
                disabled={disabled}
                onClick={() => setOpen(!open)}
              />
            )}
          </WriteAction>

          <WriteAction session={session} required="RateCard:Delete">
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete the ${label} band`}
                title="Delete this band?"
                description="Bookings already taken keep the price they were quoted. Retiring it instead leaves the history readable."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(band.id).catch(() => undefined)}
              />
            )}
          </WriteAction>
        </div>
      </div>

      {open && (
        <div className="bg-light border-border-color border-t px-3 py-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <Field label={`Price (${currency})`}>
                <input
                  type="number"
                  onWheel={(event) => event.currentTarget.blur()}
                  min={0}
                  step="0.01"
                  value={amount ?? ''}
                  className={controlClass}
                  onChange={(event) =>
                    setAmount(event.target.value === '' ? null : Number(event.target.value))
                  }
                />
              </Field>
            </div>

            {/* A per-day rate on a bounded band is a contradiction the server refuses: the band
                already covers every duration inside its bound, so the extra days do not exist. */}
            {band.upToDays === null && (
              <div className="col-span-2 sm:col-span-1">
                <Field label={`Each further day (${currency})`}>
                  <input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    step="0.01"
                    value={perExtraDay ?? ''}
                    placeholder="Flat"
                    className={controlClass}
                    onChange={(event) =>
                      setPerExtraDay(event.target.value === '' ? null : Number(event.target.value))
                    }
                  />
                </Field>
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-gray-900">
              <Switch
                size="small"
                checked={band.isActive}
                onChange={(next) =>
                  void save.mutateAsync({ isActive: next }).catch(() => undefined)
                }
              />
              Published
            </label>

            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!changed || amount === null || save.isPending}
              onClick={() =>
                void save
                  .mutateAsync({ amount: amount ?? band.amount, perExtraDay })
                  .then(() => setOpen(false))
                  .catch(() => undefined)
              }
            >
              Save band
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

/** Which request field names each level's owner. Exactly one is ever set. */
const ownerField: Record<OwnerKind, string> = {
  Category: 'categoryId',
  Item: 'itemId',
  Variant: 'variantId',
  Unit: 'itemUnitId',
};

/** Which row column holds each level's owner, for finding a card's own bands in the list. */
function ownerIdOn(row: RateSlabRow, kind: OwnerKind): number | null {
  switch (kind) {
    case 'Category':
      return row.categoryId;
    case 'Item':
      return row.itemId;
    case 'Variant':
      return row.variantId;
    default:
      return row.itemUnitId;
  }
}

/** What the editor sends. The owner and the scope come from the card, never from these fields. */
interface NewBand {
  upToDays: number | null;
  amount: number;
  perExtraDay: number | null;
}

/**
 * Publishing a band.
 *
 * The bound is the only field that is not editable afterwards, so it is the one this exists for —
 * changing a band's duration means withdrawing it and publishing another, and the page says so
 * rather than offering a control the server would ignore.
 */
function BandEditor({
  session,
  bands,
  ownerKind,
  ownerId,
  shared,
  currency,
  onPublished,
}: {
  session: Session;
  bands: readonly RateSlabRow[];
  ownerKind: OwnerKind;
  ownerId: number;
  shared: boolean;
  currency: string;
  /** Raised on the first band, which is the point the card's owner and scope become its identity. */
  onPublished: () => void;
}) {
  const [upToDays, setUpToDays] = useState<number | null>(null);
  const [openEnded, setOpenEnded] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [perExtraDay, setPerExtraDay] = useState<number | null>(null);

  const bound = openEnded ? null : upToDays;

  const add = useApiMutation<RateSlabRow, NewBand>({
    mutationFn: (draft) =>
      apiFetch<RateSlabRow>('/api/rate-slab', {
        method: 'POST',
        body: {
          amount: draft.amount,
          upToDays: draft.upToDays,
          [ownerField[ownerKind]]: ownerId,
          // Refused on a bounded band by the server, and rightly: that band already covers every
          // duration inside its bound, so the extra days it would charge for do not exist.
          perExtraDay: draft.upToDays === null ? draft.perExtraDay : null,
          shared,
        },
      }),
    success: 'Band published.',
    invalidates: [rateSlabsKey],
    onSuccess: () => {
      setUpToDays(null);
      setOpenEnded(false);
      setAmount(null);
      setPerExtraDay(null);
      onPublished();
    },
  });

  const taken = boundIsTaken(bands, bound);

  const valid =
    amount !== null && amount >= 0 && (openEnded || (upToDays !== null && upToDays >= 1)) && !taken;

  return (
    <WriteAction session={session} required="RateCard:Create">
      {(disabled) => (
        <Card>
          <SectionTitle
            trailing={
              <span className="text-default text-[11.5px]">
                {openEnded ? 'The last band' : 'A bounded band'}
              </span>
            }
          >
            Add a band
          </SectionTitle>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <Field label="Up to (days)">
                <input
                  type="number"
                  onWheel={(event) => event.currentTarget.blur()}
                  min={1}
                  step={1}
                  value={upToDays ?? ''}
                  disabled={disabled || openEnded}
                  placeholder={openEnded ? 'No limit' : '3'}
                  className={controlClass}
                  onChange={(event) =>
                    setUpToDays(event.target.value === '' ? null : Number(event.target.value))
                  }
                />
              </Field>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <Field label={`Price (${currency})`}>
                <input
                  type="number"
                  onWheel={(event) => event.currentTarget.blur()}
                  min={0}
                  step="0.01"
                  value={amount ?? ''}
                  disabled={disabled}
                  placeholder="6500"
                  className={controlClass}
                  onChange={(event) =>
                    setAmount(event.target.value === '' ? null : Number(event.target.value))
                  }
                />
              </Field>
            </div>

            {openEnded && (
              <div className="col-span-2 sm:col-span-1">
                <Field label={`Each further day (${currency})`}>
                  <input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    step="0.01"
                    value={perExtraDay ?? ''}
                    disabled={disabled}
                    placeholder="Flat"
                    className={controlClass}
                    onChange={(event) =>
                      setPerExtraDay(event.target.value === '' ? null : Number(event.target.value))
                    }
                  />
                </Field>
              </div>
            )}

            <div className="col-span-2">
              <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-gray-900">
                <Switch
                  size="small"
                  checked={openEnded}
                  disabled={disabled}
                  onChange={(next) => {
                    setOpenEnded(next);

                    if (next) {
                      setUpToDays(null);
                    } else {
                      setPerExtraDay(null);
                    }
                  }}
                />
                No limit — this is the last band
              </label>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-default max-w-md text-[11.5px]">
              {taken
                ? bound === null
                  ? 'This card already has an open-ended band. Edit that one instead — two would price the same booking differently.'
                  : `This card already prices ${String(bound)} days. Edit that band instead.`
                : openEnded
                  ? 'The last band, for anything longer than the ones above. Leave the per-day rate blank for one price whatever the duration.'
                  : 'Charged flat for any hire that fits inside this many days. The bound cannot be changed once published.'}
            </p>

            <Button
              variant="primary"
              icon="icon icon-plus"
              shortcut="save"
              data-enter-key-commit
              loading={add.isPending}
              disabled={disabled || !valid || add.isPending}
              onClick={() =>
                void add
                  .mutateAsync({ upToDays: bound, amount: amount ?? 0, perExtraDay })
                  .catch(() => undefined)
              }
            >
              Add band
            </Button>
          </div>
        </Card>
      )}
    </WriteAction>
  );
}

/**
 * What the card actually charges, worked out by the same function the counter uses.
 *
 * A ladder of three prices is easy to read and easy to get subtly wrong — a shop that means "a week
 * for ₹9,000" and types the bound as 6 finds out at the counter. This answers the only question that
 * matters about a card before a customer asks it, and it answers with `resolveSlabCharge`, so what
 * it shows is what a booking will be quoted rather than an approximation of it.
 */
function Preview({ bands, currency }: { bands: readonly RateSlabRow[]; currency: string }) {
  const [days, setDays] = useState<number | null>(3);

  const active = bands.filter((band) => band.isActive);
  const charge = resolveSlabCharge(active, days ?? 0);

  return (
    <Card>
      <SectionTitle>What it charges</SectionTitle>

      <Field label="A hire of (days)">
        <input
          type="number"
          onWheel={(event) => event.currentTarget.blur()}
          min={1}
          step={1}
          value={days ?? ''}
          className={controlClass}
          onChange={(event) =>
            setDays(event.target.value === '' ? null : Number(event.target.value))
          }
        />
      </Field>

      <div className="border-border-color bg-light mt-3 rounded-md border p-3 text-center">
        {charge ? (
          <>
            <p className="font-mono-nums text-title text-2xl font-bold">
              {currency}
              {formatAmount(charge.amount)}
            </p>
            <p className="text-default mt-1 text-[11.5px]">{slabLabel(charge)}</p>
          </>
        ) : (
          <p className="text-warning text-[12.5px]">
            Nothing this card can price — a booking that long is refused.
          </p>
        )}
      </div>
    </Card>
  );
}
